/** @format */

import { message } from "antd";
import useSWR from "swr";
import { IAccountContext } from "../../content/MainContent/AccountInfoPage/AccountContexts/AccountContexts.types";
import { IUsers } from "../../content/MainContent/AccountInfoPage/AccountInfoPage.types";
import { IApiRequestsDataPerAccount } from "../../content/MainContent/Charts/Charts.types";
import { IAccountsList } from "../../content/MainContent/MainContent.types";
import VoiceGainApplicationApi from "../VoiceGainApplicationApi";
import VoiceGainAuthSvcApi from "../VoiceGainAuthSvcApi";

//Query accounts (all accounts)
export const queryAccounts = "queryAccounts";
export const queryAccountsFetcher = async (key) => {
  try {
    let response = await VoiceGainAuthSvcApi.getAccounts(true);
    if (response.ok) {
      let data = await response.json();

      let newData;

      if (data.length) {
        newData = data.map((d: any) => {
          if (!d.lastLoginDate) {
            d.lastLoginDate = "1971-01-01T00:00:00";
          }

          if (!d.dateCreated) {
            d.lastLoginDate = "1971-01-01T00:00:00";
          }

          return d;
        });
      }

      return data;
    } else throw new Error("Unable to get accounts.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};
export const useQueryAccounts = () => {
  const { data, error, mutate, isValidating } = useSWR<IAccountsList[], Error>(
    [queryAccounts],
    (key) => queryAccountsFetcher(key),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );

  return {
    accounts: data,
    isLoading: !data && !error,
    isError: error,
    mutate,
    isValidating,
  };
};

//Fetch account (single account)
export const fetchAccount = "fetchAccount";
export const fetchAccountFetcher = async ([key, accountId]) => {
  try {
    let response = await VoiceGainAuthSvcApi.getSingleAccount(accountId);
    if (response.ok) {
      let data = await response.json();
      return data;
    } else throw new Error("Unable to get account.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};

export const useFetchAccount = (accountId: string) => {
  const { data, error } = useSWR<IAccountsList, Error>(
    [fetchAccount, accountId],
    fetchAccountFetcher,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );
  return {
    account: data,
    isLoading: !data && !error,
    isError: error,
  };
};

//Query all users
export const queryUsers = "queryUsers";
export const queryUsersFetcher = async (key) => {
  try {
    let response = await VoiceGainAuthSvcApi.queryUsers();
    if (response.ok) {
      let data = await response.json();
      return data;
    } else throw new Error("Unable to get users from account.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};
export const useQueryUsers = () => {
  const { data, error, mutate, isValidating } = useSWR<IUsers[], Error>(
    [queryUsers],
    (key) => queryUsersFetcher(key),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );
  return {
    users: data,
    isLoading: !data && !error,
    isError: error,
    mutate,
    isValidating,
  };
};

//Fetch users on an account
export const fetchAccountUsers = "fetchAccountUsers";
export const fetchAccountUsersFetcher = async ([key, accountId]) => {
  try {
    let response = await VoiceGainAuthSvcApi.getUsersFromAccount(accountId);
    if (response.ok) {
      let data = await response.json();
      return data;
    } else throw new Error("Unable to get users from account.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};

export const useFetchAccountUsers = (accountId: string) => {
  const { data, error, mutate } = useSWR<IUsers[], Error>(
    [fetchAccountUsers, accountId],
    fetchAccountUsersFetcher,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );
  return {
    users: data,
    isLoading: !data && !error,
    isError: error,
    mutate,
  };
};

//Fetch Api Requests Count
export const fetchApiRequestsCount = "fetchApiRequestsCount";
export const fetchApiRequestsCountFetcher = async (key) => {
  try {
    let response = await VoiceGainApplicationApi.fetchApiRequestsCount();
    if (response.ok) {
      let data = await response.json();
      return data;
    } else throw new Error("Unable to get ASR request data.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};
export const useFetchApiRequestsCount = () => {
  const { data, error } = useSWR<IApiRequestsDataPerAccount[], Error>(
    [fetchApiRequestsCount],
    (key) => fetchApiRequestsCountFetcher(key),
    { shouldRetryOnError: false, revalidateOnFocus: true }
  );
  return {
    data,
    isLoading: !data && !error,
    isError: error,
  };
};

//Fetch contexts
export const fetchContexts = "fetchContexts";
export const fetchContextsFetcher = async (key) => {
  try {
    let response = await VoiceGainApplicationApi.getContexts();
    if (response.ok) {
      let data = await response.json();
      return data;
    } else throw new Error("Unable to get contexts.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};
export const useFetchContexts = () => {
  const { data, error } = useSWR<IAccountContext[], Error>(
    [fetchContexts],
    (key) => fetchContextsFetcher(key),
    { shouldRetryOnError: false, revalidateOnFocus: true }
  );
  return {
    contexts: data,
    isLoading: !data && !error,
    isError: error,
  };
};

//Fetch edge deployments
export const fetchEdgeDeployments = "fetchEdgeDeployments";
export const fetchEdgeDeploymentsFetcher = async (key) => {
  try {
    let response = await VoiceGainApplicationApi.getContexts();
    if (response.ok) {
      let data = await response.json();
      return data;
    } else throw new Error("Unable to get contexts.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};
export const useFetchEdgeDeployments = () => {
  const { data, error } = useSWR<IAccountContext[], Error>(
    [fetchEdgeDeployments],
    (key) => fetchEdgeDeploymentsFetcher(key),
    { shouldRetryOnError: false, revalidateOnFocus: true }
  );
  return {
    edgeDeployments: data,
    isLoading: !data && !error,
    isError: error,
  };
};

//Fetch recach
export const fetchRecacheFetcher = async () => {
  try {
    let response = await VoiceGainAuthSvcApi.recacheFetch();
    if (response.ok) {
      let data = await response.json();
      return { success: true, data };
    } else throw new Error("Unable to recache.");
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      message.error(err.message);
      throw new Error(err.message);
    }
  }
};
